import { useEffect, useState } from "react";
import { Button, Input, Table } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { HlaDefaultValues } from "../../models/Project";
import { useStores } from "../../../shared/stores/RootStore";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  HlaWayType,
} from "../../../hla/models/HlaWay";
import { capitalize } from "../../../shared/utils/format-utils";
import { toast, toaster } from "../../../shared/views/CustomToaster";
import { ProjectApi } from "../../services/ProjectApi";
import { JobStateApi } from "../../../shared/services/JobStateApi";

const roadTypes: (keyof HlaDefaultValues)[] = [
  "motorway",
  "primary",
  "secondary",
  "tertiary",
  "service",
  "unclassified",
];

const getEmptyValues = (): HlaDefaultValues => ({
  motorway: 0,
  primary: 0,
  secondary: 0,
  tertiary: 0,
  service: 0,
  unclassified: 0,
});

const ProjectDefaultsModalBase = () => {
  const { projectStore, hlaStore, campaignStore } = useStores();
  const { defaults } = projectStore;

  const [averageRoadSpeed, setAverageRoadSpeed] = useState(getEmptyValues());
  const [averageDailyTraffic, setAverageDailyTraffic] = useState(
    getEmptyValues(),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (defaults) {
      setAverageRoadSpeed(defaults.averageRoadSpeed);
      setAverageDailyTraffic(defaults.averageDailyTraffic);
    }
  }, [defaults]);

  const handleClose = () => {
    projectStore.setShowProjectDefaultsModal(false);
  };

  const handleSubmit = async () => {
    const projectId = projectStore.selectedId!;
    setSubmitting(true);
    const toastId = toast("loading", "Updating scores");
    projectStore
      .updateDefaults(projectId, {
        averageRoadSpeed,
        averageDailyTraffic,
      })
      .then(async () => {
        toaster.update(toastId, {
          title: "Updating scores...",
        });
        const jobStateId = await ProjectApi.refreshHlaScores(projectId).then(
          (response) => response.data,
        );

        await JobStateApi.poll(jobStateId, 2000, (jobState) => {
          toaster.update(toastId, {
            title: `Updating scores (${jobState.progressValue}, ${jobState.progressTotal})`,
          });
        });

        handleClose();

        toaster.update(toastId, {
          title: "Fetching new scores...",
        });
        await hlaStore.fetchAll(campaignStore.selectedId!);

        toaster.update(toastId, {
          type: "success",
          title: "All done",
        });
      })
      .catch(() => {
        toaster.update(toastId, {
          type: "error",
          title: "Unable to save",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <CustomDialog
      size="xl"
      header="Set project default values"
      footer={
        <Button
          colorPalette="primary"
          onClick={handleSubmit}
          loading={submitting}
        >
          Save
        </Button>
      }
      onClose={handleClose}
    >
      <Table.Root>
        <Table.Caption />
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader></Table.ColumnHeader>
            <Table.ColumnHeader>
              Average road speed ({projectStore.speedUnitStr})
            </Table.ColumnHeader>
            <Table.ColumnHeader>Average daily traffic</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {roadTypes.map((type) => {
            const hlaWayType = capitalize(type) as HlaWayType;
            return (
              <Table.Row key={type}>
                <Table.Cell>{hlaWayType}</Table.Cell>
                <Table.Cell>
                  <Input
                    type="number"
                    step={10}
                    min={0}
                    colorPalette="primary"
                    placeholder={getDefaultAverageRoadSpeed(
                      hlaWayType,
                      projectStore.distanceUnit,
                    ).toString()}
                    value={averageRoadSpeed[type] ?? ""}
                    onChange={(e) => {
                      const { value } = e.target;
                      setAverageRoadSpeed((prev) => ({
                        ...prev,
                        [type]: value === "" ? null : parseInt(value),
                      }));
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="number"
                    step={100}
                    min={0}
                    colorPalette="primary"
                    placeholder={getDefaultAverageDailyTraffic(
                      hlaWayType,
                    ).toString()}
                    value={averageDailyTraffic[type] ?? ""}
                    onChange={(e) => {
                      const { value } = e.target;
                      setAverageDailyTraffic((prev) => ({
                        ...prev,
                        [type]: value === "" ? null : parseInt(value),
                      }));
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </CustomDialog>
  );
};

export const ProjectDefaultsModal = observer(ProjectDefaultsModalBase);
